<template>
  <div class="banner-container bg-[#F4F1FF] py-6 mb-8 border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
      <div class="flex flex-col md:flex-row items-center justify-center w-full gap-4 max-w-3xl">
        <div class="w-full md:flex-1">
          <LocationFilter
            ref="locationFilterRef"
            :initial-state="props.initialState"
            :initial-city="props.initialCity"
            :pre-fetched-cities="props.preFetchedCities"
            :states-cities="props.statesCities"
            :permalink="props.permalink"
            @update:state="handleStateSelect"
            @update:state-input="handleStateInput"
            @update:city="handleCitySelect"
            @update:city-input="handleCityInput"
            @update:state-exact-match="handleStateExactMatch"
            @update:city-exact-match="handleCityExactMatch"
          />
        </div>
        <div class="w-full md:w-48">
          <ClientOnly>
            <NuxtLink
              :to="filterUrl"
              :class="[
                'rounded-lg px-4 py-2 text-center text-sm font-medium block',
                isValidSelection
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              ]"
              :aria-disabled="!isValidSelection"
              @click.prevent="!isValidSelection ? null : true"
            >
              Apply Filters
            </NuxtLink>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue'
import LocationFilter from '../filters/LocationFilter.vue'
import type { City } from '../filters/CityAutocomplete.vue'
import { stateOptions } from '~/utils/states'
import { buildCompanyUrl } from '~/utils/formatters'

const props = defineProps({
  permalink: {
    type: String,
    required: true
  },
  initialState: {
    type: String,
    default: ''
  },
  initialCity: {
    type: String,
    default: ''
  },
  preFetchedCities: {
    type: Array as PropType<City[]>,
    default: () => []
  },
  statesCities: {
    type: Object as PropType<Record<string, City[]>>,
    default: () => ({})
  }
})
const locationFilterRef = ref<InstanceType<typeof LocationFilter> | null>(null)
const selectedState = ref(
  props.initialState
    ? stateOptions.find(s => s.code === props.initialState)
    : null
)
const stateIsValid = ref(true)
const cityIsValid = ref(true)
const filters = reactive({
  state: props.initialState || '',
  city: props.initialCity || ''
})
const originalFilters = reactive({
  state: props.initialState || '',
  city: props.initialCity || ''
})
const isValidSelection = computed(() => {
  const hasChanges
    = filters.state !== originalFilters.state
      || filters.city !== originalFilters.city
  if (!hasChanges) return false
  if (filters.state.trim() && !stateIsValid.value) return false
  if (filters.city.trim()) {
    if (!cityIsValid.value) return false
    if (!filters.state.trim() || !stateIsValid.value) return false
  }
  return true
})
function handleStateSelect(state) {
  selectedState.value = state
  filters.state = state?.code || ''
  stateIsValid.value = !!state
  if (!state) {
    filters.city = ''
    cityIsValid.value = true
  }
}
function handleStateInput(value) {
  filters.state = value
  if (!value) {
    selectedState.value = null
    filters.city = ''
    cityIsValid.value = true
  }
}
function handleCitySelect(city) {
  filters.city = city ? city.name : ''
  cityIsValid.value = !!city
}
function handleCityInput(value) {
  filters.city = value
}
function handleStateExactMatch(hasMatch) {
  stateIsValid.value = hasMatch
  if (!hasMatch && filters.city) {
    filters.city = ''
    cityIsValid.value = true
  }
}
function handleCityExactMatch(hasMatch) {
  cityIsValid.value = hasMatch
}
const filterUrl = computed(() => {
  const stateCode = selectedState.value?.code
  const city = filters.city?.trim()
  return buildCompanyUrl(props.permalink, 'jobs', {
    city: city || undefined,
    stateCode: stateCode || undefined
  })
})
</script>

<style scoped>
.banner-container {
  box-sizing: border-box;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}
</style>
